package com.downdogapp.client.widget

import com.downdogapp.*
import com.downdogapp.AlignItems.CENTER
import com.downdogapp.AlignItems.FLEX_START
import com.downdogapp.FontWeight.MEDIUM
import com.downdogapp.FontWeight.REGULAR
import com.downdogapp.Overflow.HIDDEN
import com.downdogapp.Size.Companion.percent
import com.downdogapp.Size.Companion.px
import com.downdogapp.Size.INHERIT
import com.downdogapp.Size.Pixels
import com.downdogapp.TextOverflow.ELLIPSIS
import com.downdogapp.WhiteSpace.NOWRAP
import com.downdogapp.client.AbstractView
import com.downdogapp.client.resources.Image
import com.downdogapp.client.resources.Images
import org.w3c.dom.HTMLDivElement
import org.w3c.dom.HTMLElement

class TableRowInit(val height: Pixels?, val allowSeparator: Boolean, private val createRow: () -> TableRow) {

  fun addToParent(parent: HTMLElement, separator: TableSeparator?) {
    val row = createRow()
    height?.let {
      row.root.height = it
    }
    separator?.let {
      row.setSeparator(separator)
    }
    parent.appendChild(row.root)
  }
}

abstract class TableRow : AbstractView() {

  abstract fun setSeparator(separator: TableSeparator) // Override for separator styling, etc
}

class CustomRow private constructor(init: CustomRow.() -> Unit = {}) : TableRow() {

  override val root = createDiv()

  init {
    this.init()
  }

  override fun setSeparator(separator: TableSeparator) {
    error("Separators are not supported for Custom Table Rows")
  }

  companion object {

    fun customRow(height: Pixels?, init: CustomRow.() -> Unit = {}) =
      TableRowInit(height = height, allowSeparator = false) { CustomRow(init) }

    fun headerRow(title: String?, height: Pixels = px(58)) =
      customRow(height) {
        root.apply {
          title?.let {
            centerInParent(blockClicks = false) {
              marginTop = px(18)
              textAlign = TextAlign.CENTER
              label(title.uppercase(), 16, FontWeight.MEDIUM)
            }
          }
        }
      }
  }
}

class StructuredRow private constructor(clickFn: (() -> Unit)?,
                                        init: StructuredRow.() -> Unit) : TableRow() {

  private var iconContainer: HTMLDivElement
  private var textContainer: HTMLDivElement
  private var badgeContainer: HTMLDivElement
  private var rightContainer: HTMLDivElement

  override val root = createDiv {
    width = percent(100)

    horizontalFlexBox(verticalAlign = CENTER) {
      height = INHERIT
      marginLeft = HORIZONTAL_CONTENT_GUTTER_PX

      iconContainer = div()
      textContainer = verticalFlexBox(horizontalAlign = FLEX_START)
      badgeContainer = div()
      div {
        // slack view
        flexGrow = 1.0
      }
      rightContainer = div()
    }

    clickFn?.let {
      onClick {
        it()
      }
    }
  }

  init {
    this.init()
  }

  override fun setSeparator(separator: TableSeparator) {
    root.div {
      height = separator.height
      bgColor = separator.color
      marginLeft = separator.marginLeft
      marginRight = separator.marginRight
      marginTop = separator.height * -1
    }
  }

  fun highlight() {
    root.apply {
      bgColor = Color.grey(0.2)
    }
  }

  fun leftImage(image: Image) =
    iconContainer.imageView(image) {
      marginRight = px(10)
    }

  fun leftImage(imageSrc: String, imageWidth: Int = 40, imageHeight: Int = 40, marginRight: Int = 10) =
    iconContainer.imageView(width = imageWidth, height = imageHeight) {
      src = imageSrc
      this.marginRight = px(marginRight)
    }

  fun leftTextContent(text: String?,
                      subtext: String? = null,
                      fontSize: Int = 16,
                      subtextFontSize: Int = 12,
                      textWeight: FontWeight = MEDIUM,
                      subtextWeight: FontWeight = REGULAR,
                      subtextColor: Color = Color.WHITE,
                      subtextMaxHeight: Int? = null) {
    textContainer.apply {
      overflowX = HIDDEN

      label(text, fontSize, textWeight) {
        width = percent(100)
        whiteSpace = NOWRAP
        overflowX = HIDDEN
        textOverflow = ELLIPSIS
      }

      subtext?.let {
        label(subtext, subtextFontSize, subtextWeight) {
          marginTop = px(2)
          txtColor = subtextColor
          textOverflow = ELLIPSIS
          subtextMaxHeight?.let { maxHeight = px(it) }
        }
      }
    }
  }

  fun setRightMargin(constant: Pixels) {
    rightContainer.marginLeft = constant
  }

  fun rightTextContent(text: String?) {
    rightContainer.label(text, 16) {
      marginRight = HORIZONTAL_CONTENT_GUTTER_PX
    }
  }

  fun rightToggle(getValue: () -> Boolean,
                  onValueChanged: (Boolean) -> Unit): Toggle {
    rightContainer.marginRight = px(16)
    return rightContainer.toggle(getValue, onValueChanged)
  }

  private fun rightIcon(image: Image, marginRight: Int) =
    rightContainer.imageView(image) {
      this.marginRight = px(marginRight)
    }

  fun rightArrow() =
    rightIcon(Images.SETTINGS_RIGHT_ARROW, 4)

  fun rightCheckmark() =
    rightIcon(Images.SETTINGS_CHECKMARK, 7)

  fun rightDownArrow() =
    rightIcon(Images.DOWN_ARROW_ICON, 7)

  fun rightUpArrow() =
    rightIcon(Images.UP_ARROW_ICON, 7)

  companion object {

    fun structuredRow(height: Pixels?,
                      allowSeparator: Boolean,
                      clickFn: (() -> Unit)? = null,
                      init: StructuredRow.() -> Unit) =
      TableRowInit(height = height, allowSeparator = allowSeparator) {
        StructuredRow(clickFn, init)
      }

    fun leftIconRow(image: Image,
                    text: String,
                    subText: String? = null,
                    fontSize: Int = 16,
                    clickFn: (() -> Unit)? = null) =
      structuredRow(DEFAULT_ROW_HEIGHT_PX, true, clickFn) {
        leftImage(image)
        leftTextContent(text, subText, fontSize)

        if (clickFn != null) {
          rightArrow()
        }
      }

    fun selectionRow(leftText: String?, rightText: String?, clickFn: () -> Unit): TableRowInit =
      structuredRow(SELECTION_ROW_HEIGHT_PX, true, clickFn) {
        leftTextContent(leftText)
        rightTextContent(rightText)
      }
  }
}

val DEFAULT_ROW_HEIGHT_PX = px(60)
val SELECTION_ROW_HEIGHT_PX = px(51)
